import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { COMPANION_ART } from './art';
import { advanceStreak, goalReached, streakMilestone } from './companionRules';
import { Button } from './ui';
import { LVScope, useTheme } from './LVTheme';

export const REACTIONS = {
  welcome: 'Ready for our next step? I’m right here with you.',
  proud: 'I am so proud of you. Another step closer to your goal!',
  encourage: 'A missed answer isn’t a setback. Let’s learn from it together.',
  streak5: 'Five days in a row! Your consistency is becoming your superpower.',
  streak10: 'TEN days! Look how far you’ve come. I am so proud of you!',
  goal: 'You did it! You reached the goal you set. Let’s celebrate!',
};

const noop = () => {};
const Context = createContext({
  reaction: { mood: 'welcome', text: REACTIONS.welcome }, popup: false, count: 0, goal: 3, streak: 0,
  dismiss: noop, setGoal: noop, react: noop, celebrate: noop, preview: noop, complete: noop,
});
export const useCompanion = () => useContext(Context);

export function CompanionProvider({ children }) {
  const [reaction, setReaction] = useState({ mood: 'welcome', text: REACTIONS.welcome });
  const [popup, setPopup] = useState(false);
  const [goal, setGoal] = useState(3);
  const [completed, setCompleted] = useState([]);
  const [activity, setActivity] = useState({ day: null, streak: 0 });

  // The companion speaks only at an achievement, as a pop-up in front of the student (never on every answer).
  function react(mood, text = REACTIONS[mood]) { setReaction({ mood, text }); setPopup(true); }
  // A finished lesson, set, quiz or exam module. Counts once per id; the streak is days in a row (Africa/Cairo), session-only.
  function complete(id, label) {
    if (completed.includes(id)) return;
    const next = completed.length + 1;
    setCompleted([...completed, id]);
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const days = advanceStreak(activity.day, activity.streak, today);
    setActivity({ day: today, streak: days });
    const milestone = activity.day !== today ? streakMilestone(days) : null;
    if (goalReached(completed.length, next, goal)) react('goal');
    else if (milestone) react(milestone);
    else react('proud', `${label} complete! I am so proud of you. ${Math.min(next, goal)} of ${goal} steps toward your goal.`);
  }
  const value = {
    reaction, popup, count: completed.length, goal, streak: activity.streak,
    dismiss: () => setPopup(false),
    setGoal: (n) => { if (Number.isInteger(n) && n >= 1 && n <= 20) setGoal(n); },
    react, celebrate: react, complete,
    preview: (mood) => { setReaction({ mood, text: REACTIONS[mood], preview: true }); setPopup(true); },
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

const ANIMAL = { ember: 'dragon', forest: 'fox', gold: 'lion', aurora: 'owl' };

export function CompanionImage({ mood, className = '' }) {
  const { palette } = useTheme();
  const animal = ANIMAL[palette] || 'dragon';
  return <img src={COMPANION_ART[animal][mood]} alt={`${animal} companion feeling ${mood}`} width={384} height={384} className={`companion-image object-contain ${className}`} />;
}

export function CompanionCloud() {
  const { reaction } = useCompanion();
  return (
    <div className="flex max-w-xl items-center gap-2">
      <div role="status" className="companion-cloud relative min-w-0 flex-1 border border-border bg-popover px-5 py-4 text-popover-foreground shadow-md">
        <p className="text-sm leading-relaxed">{reaction.text}</p>
      </div>
      <CompanionImage mood={reaction.mood} className="h-28 w-28 shrink-0 sm:h-36 sm:w-36" />
    </div>
  );
}

// The pop-up: the companion's PNG and one short message in a small window in front of the page. Closes with its button,
// Escape or a click outside. Mounted once for the whole app; has its own token scope.
export function CompanionOverlay() {
  const { popup, reaction, dismiss } = useCompanion();
  const button = useRef(null);
  useEffect(() => {
    if (!popup) return undefined;
    const before = document.activeElement;
    if (button.current) button.current.focus();
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); if (before && before.focus) before.focus(); };
    // eslint-disable-next-line
  }, [popup]);
  if (!popup) return null;
  return (
    <LVScope className="!bg-transparent">
      <div className="fixed inset-0 z-[60] grid place-items-center bg-foreground/45 p-4" data-testid="companion-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) dismiss(); }}>
        <div role="dialog" aria-modal="true" aria-label="A message from your companion" className="companion-popup w-full max-w-sm rounded-2xl border border-border bg-popover p-6 text-center text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95">
          <CompanionImage mood={reaction.mood} className="mx-auto h-44 w-44" />
          <p className="mt-3 font-serif text-2xl leading-snug">{reaction.text}</p>
          <Button ref={button} className="mt-6 w-full rounded-full" onClick={dismiss}>{reaction.mood === 'welcome' ? 'Let’s go' : 'Keep going'}</Button>
        </div>
      </div>
    </LVScope>
  );
}

// Greets a signed-in student once per browser session, when they enter the course.
const WELCOME_KEY = 'preppeak-welcomed';
export function CompanionWelcome({ firstTime = false }) {
  const { celebrate } = useCompanion();
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem(WELCOME_KEY) === '1'; } catch { /* unavailable storage greets once per page */ }
    if (seen) return undefined;
    const t = setTimeout(() => {
      try { sessionStorage.setItem(WELCOME_KEY, '1'); } catch { /* ignore */ }
      celebrate('welcome', firstTime ? 'Welcome! I’m proud of you for starting. Let’s get started.' : REACTIONS.welcome);
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);
  return null;
}
